// Reforma UX §6.2 — título editável inline (lápis → input + ✓ salvar / ✕ cancelar).
// Usado no card da listagem (SESS-01/02) e no header do chat (SESS-04).
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EditableTitleProps {
  value: string;
  /** Persiste o novo título. Rejeição = mantém o título anterior (caller mostra toast). */
  onSave: (title: string) => Promise<void>;
  className?: string;
  textClassName?: string;
  /** aria-label do botão de lápis. */
  editLabel?: string;
}

export function EditableTitle({
  value,
  onSave,
  className,
  textClassName,
  editLabel = 'Editar título',
}: EditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function startEdit(e: MouseEvent) {
    e.stopPropagation();
    setDraft(value);
    setEditing(true);
  }

  function cancel(e?: MouseEvent) {
    e?.stopPropagation();
    setEditing(false);
    setDraft(value);
  }

  async function save(e?: MouseEvent) {
    e?.stopPropagation();
    const next = draft.trim();
    if (!next || next === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
    } finally {
      // Sucesso ou falha, sai do modo edição — em falha o caller mostra toast
      // e o `value` (não alterado) volta a ser exibido.
      setSaving(false);
      setEditing(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    // BUG DO ESPAÇO (correção v2 §5.2.2): o card pai é div[role=button] com
    // onKeyDown que captura ' ' — sem o stopPropagation TOTAL aqui, o espaço
    // borbulha, o card faz preventDefault e o input nunca recebe o caractere.
    // Defesa 1 de 2 (a outra: o card ignora eventos vindos de filhos).
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  }

  if (editing) {
    return (
      <span
        className={cn('flex items-center gap-1 min-w-0', className)}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={saving}
          aria-label="Novo título da sessão"
          className={cn(
            'flex-1 min-w-0 bg-raios-elevated/60 border border-raios-border rounded px-1.5 py-0.5',
            'text-raios-text focus:outline-none focus:ring-1 focus:ring-raios-accent/50',
            textClassName,
          )}
        />
        <button
          type="button"
          onClick={(e) => void save(e)}
          disabled={saving}
          aria-label="Salvar título"
          className="wf-miniedit wf-miniedit--ok shrink-0"
        >
          <Check />
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          aria-label="Cancelar edição"
          className="wf-miniedit wf-miniedit--no shrink-0"
        >
          <X />
        </button>
      </span>
    );
  }

  return (
    <span className={cn('flex items-center gap-1 min-w-0', className)}>
      <span className={cn('truncate', textClassName)}>{value}</span>
      <button
        type="button"
        onClick={startEdit}
        aria-label={editLabel}
        title={editLabel}
        className="wf-miniedit shrink-0"
      >
        <Pencil />
      </button>
    </span>
  );
}
